<script>
  import Layout from './Layout.svelte';
  import PageLoader from './PageLoader.svelte';
  import FeatureUnavailable from '../routes/FeatureUnavailable.svelte';
  import { AlertTriangle } from '../lib/icons/index.js';
  import { capabilities, capabilitiesStatus, refreshCapabilities } from '../stores/capabilities.js';
  import { activeCompanyId, companiesStatus, loadCompanies } from '../stores/company.js';
  import { companyState, featureAccess } from '../lib/features.js';
  import { t } from '../i18n/index.js';

  /**
   * The page of a route that exists only for accounts holding a gated feature
   * (lib/features.js). It is the component the route points at, and it shows
   * what the stores say, as they arrive:
   *
   *   - waiting: a loader, with no deadline — a slow answer opens the page
   *     when it comes, including a retry that is still in flight;
   *   - something could not be read — the capabilities, or the list of
   *     companies they depend on: a message with a retry button, never the
   *     «funzione non disponibile» page, which is only for an explicit no;
   *   - the server said the account lacks the feature: that page, whatever
   *     the account's role (also when the account really has no company);
   *   - it has the feature: the page.
   */
  export let feature;
  export let component;
  export let params = {};

  $: company = companyState($activeCompanyId, $companiesStatus);
  $: access = featureAccess($capabilities, $capabilitiesStatus, feature, { company });
  // The companies come first: retrying the capabilities of a company that is not known would ask nothing.
  const retry = () => (company === 'error' ? loadCompanies() : refreshCapabilities());
</script>

{#if access === 'enabled'}
  <svelte:component this={component} {params} />
{:else if access === 'loading'}
  <Layout><PageLoader /></Layout>
{:else if access === 'error'}
  <Layout>
    <div class="flex flex-col items-center text-center py-20" role="alert" data-testid="feature-load-error">
      <AlertTriangle size={40} class="mb-4 text-action/70" aria-hidden="true" />
      <h1 class="section-title">{$t('features.gate.loadErrorTitle')}</h1>
      <p class="text-muted text-sm max-w-md">{$t('features.gate.loadErrorBody')}</p>
      <button class="btn btn-primary mt-6" on:click={retry}>{$t('features.gate.retry')}</button>
    </div>
  </Layout>
{:else}
  <FeatureUnavailable params={{ feature }} />
{/if}
